import { DOCUMENT } from '@angular/common';
import {
  ApplicationRef,
  Component,
  ContentChild,
  EmbeddedViewRef,
  EventEmitter,
  Inject,
  OnDestroy,
  Output,
  Renderer2,
  TemplateRef
} from '@angular/core';
import { arrow, autoUpdate, computePosition, flip, offset, Placement, shift } from '@floating-ui/dom';
import { ZIndexUtils } from 'primeng/utils';
import { NxClosable, NxPopoverService } from './nx-popover.service';

const ARROW_SIZE = 10;
const TRIGGER_GAP = ARROW_SIZE + 2;

/**
 * CDK-free anchored popover built on @floating-ui/dom.
 *
 * - Content is provided as a lazy `<ng-template>` (only instantiated while open).
 * - Panel + dimmed backdrop are portalled to `document.body` (manual portal via Renderer2 +
 *   ApplicationRef) so they escape every scroll/transform container.
 * - `position: fixed` + floating-ui (`offset`/`flip`/`shift`/`arrow` + `autoUpdate`) gives a
 *   side placement, vertically centred on the trigger, that flips/clamps to the viewport and
 *   re-aligns on scroll, resize and content growth.
 *
 * Usage:
 * ```html
 * <nx-popover #pop (closed)="reload()">
 *   <ng-template><app-file-list .../></ng-template>
 * </nx-popover>
 * <button (click)="pop.toggle($event.currentTarget)">…</button>
 * ```
 */
@Component({
  selector: 'nx-popover',
  template: ''
})
export class NxPopoverComponent implements OnDestroy, NxClosable {
  @ContentChild(TemplateRef) private content!: TemplateRef<unknown>;
  @Output() closed = new EventEmitter<void>();

  private trigger?: HTMLElement;
  private backdrop?: HTMLElement;
  private panel?: HTMLElement;
  private arrowEl?: HTMLElement;
  private viewRef?: EmbeddedViewRef<unknown>;
  private stopAutoUpdate?: () => void;
  private unlistenKeydown?: () => void;

  constructor(
    private renderer: Renderer2,
    private appRef: ApplicationRef,
    private popoverService: NxPopoverService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  get isOpen(): boolean {
    return !!this.panel;
  }

  toggle(trigger: EventTarget | HTMLElement | null): void {
    this.isOpen ? this.close() : this.open(trigger);
  }

  open(trigger: EventTarget | HTMLElement | null): void {
    if (this.isOpen || !(trigger instanceof HTMLElement) || !this.content) {
      return;
    }
    this.popoverService.register(this);
    this.trigger = trigger;

    // dimmed backdrop
    this.backdrop = this.renderer.createElement('div');
    this.renderer.addClass(this.backdrop, 'nx-popover-backdrop');
    this.renderer.listen(this.backdrop, 'click', () => this.close());

    // panel (overflow: visible so the arrow can poke out) + arrow
    this.panel = this.renderer.createElement('div');
    this.renderer.addClass(this.panel, 'nx-popover-panel');
    this.renderer.setAttribute(this.panel, 'role', 'dialog');
    this.arrowEl = this.renderer.createElement('div');
    this.renderer.addClass(this.arrowEl, 'nx-popover-arrow');
    this.renderer.appendChild(this.panel, this.arrowEl);

    // inner, scrollable content wrapper (keeps the arrow from being clipped by overflow)
    const contentWrapper = this.renderer.createElement('div');
    this.renderer.addClass(contentWrapper, 'nx-popover-content');
    this.viewRef = this.content.createEmbeddedView({});
    this.appRef.attachView(this.viewRef);
    this.viewRef.rootNodes.forEach((node) => this.renderer.appendChild(contentWrapper, node));
    this.renderer.appendChild(this.panel, contentWrapper);

    this.renderer.appendChild(this.document.body, this.backdrop);
    this.renderer.appendChild(this.document.body, this.panel);

    // z-index via PrimeNG's shared stack: popover sits above the current stack (incl. dialogs),
    // and any overlay opened afterwards from inside (menus/dropdowns) lands above the popover.
    ZIndexUtils.set('modal', this.panel, 1100);
    this.renderer.setStyle(this.backdrop, 'z-index', `${Number(this.panel.style.zIndex) - 1}`);

    this.unlistenKeydown = this.renderer.listen(this.document, 'keydown', (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        this.close();
      }
    });

    this.stopAutoUpdate = autoUpdate(this.trigger, this.panel, () => this.updatePosition());
  }

  close(): void {
    if (!this.isOpen) {
      return;
    }
    this.stopAutoUpdate?.();
    this.unlistenKeydown?.();
    if (this.viewRef) {
      this.appRef.detachView(this.viewRef);
      this.viewRef.destroy();
    }
    if (this.panel) {
      ZIndexUtils.clear(this.panel);
    }
    this.backdrop?.remove();
    this.panel?.remove();
    this.stopAutoUpdate = this.unlistenKeydown = undefined;
    this.viewRef = this.backdrop = this.panel = this.arrowEl = this.trigger = undefined;
    this.popoverService.unregister(this);
    this.closed.emit();
  }

  ngOnDestroy(): void {
    this.close();
  }

  private updatePosition(): void {
    if (!this.trigger || !this.panel || !this.arrowEl) {
      return;
    }
    const placement: Placement = 'right';
    computePosition(this.trigger, this.panel, {
      strategy: 'fixed',
      placement,
      middleware: [
        offset(TRIGGER_GAP),
        flip({ fallbackPlacements: ['left'] }),
        shift({ padding: 8 }),
        arrow({ element: this.arrowEl, padding: 8 })
      ]
    }).then(({ x, y, placement: finalPlacement, middlewareData }) => {
      if (!this.panel || !this.arrowEl) {
        return;
      }
      this.renderer.setStyle(this.panel, 'left', `${x}px`);
      this.renderer.setStyle(this.panel, 'top', `${y}px`);

      const side = finalPlacement.split('-')[0]; // 'right' | 'left'
      this.renderer.setAttribute(this.panel, 'data-placement', side);

      const arrowData = middlewareData.arrow;
      const staticSide = side === 'right' ? 'left' : 'right';
      this.renderer.setStyle(this.arrowEl, 'top', arrowData?.y != null ? `${arrowData.y}px` : '');
      this.renderer.setStyle(this.arrowEl, 'left', '');
      this.renderer.setStyle(this.arrowEl, 'right', '');
      this.renderer.setStyle(this.arrowEl, staticSide, `${-ARROW_SIZE / 2}px`);
    });
  }
}
